import { Box, Button, Flex, Modal, styled, Text } from '@driftt/dds'
import { showTransientNotification } from '@driftt/traffic-control'
import { toLower } from 'ramda'
import React, { useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'
import sanitizeHtml from 'sanitize-html'

import {
  validateDescription,
  validateExit,
  validateInstructions,
} from 'api/scenarios'

import TextareaWithButton from './TextareaWithButton'

type ModalType = 'description' | 'instructions' | 'exit'

interface ValidationModalProps {
  isOpen: boolean
  onClose: () => void
  onAction: (
    prompt: string,
    phrases?: string[],
    replyGuidelines?: string
  ) => void
  type: ModalType
  savedValidaton?: string
  defaultValidation?: ValidationObject
}

export interface ValidationObject {
  prompt: string
  response?: string
  replyGuidelines?: string
  examples?: string[]
}

const ScrollbarHiddenContainer = styled(Box, {
  overflow: 'hidden',
  width: '100%',
  marginTop: '40px',
})

const Container = styled(Box, {
  width: '100%',
  height: '527px',
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
})

const InfoContainer = styled(Box, {
  backgroundColor: '$blue1',
  border: 'solid 1px $blue3',
  borderRadius: '3px',
  padding: '12px',
})

const StyledList = styled('ul', {
  listStyleType: 'disc',
  paddingLeft: '20px',
})

const Divider = styled(Box, {
  width: '100%',
  borderTop: '1px solid $gray3',
})

const sanitizeOptions = {
  allowedTags: ['a'],
  allowedAttributes: { a: ['href'] },
}

const ResponseContainer = ({ children }: { children: string }) => {
  const sanitizedResponse = children
    ? sanitizeHtml(children, sanitizeOptions)
    : ''

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: sanitizedResponse }}
      css={{ whiteSpace: 'pre-line' }}
    ></Box>
  )
}
const PromptContainer = ({
  children,
  cssStyles,
}: {
  children?: string
  cssStyles?: React.CSSProperties
}) => {
  const sanitizedContent = children
    ? sanitizeHtml(children, sanitizeOptions)
    : ''

  const containerStyles: React.CSSProperties = {
    backgroundColor: '$gray2',
    borderLeft: 'solid 1px $gray10',
    width: '100%',
    padding: '12px',
    whiteSpace: 'pre-line',
    ...cssStyles,
  }

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      css={{ ...containerStyles }}
    />
  )
}

const configByType = {
  description: {
    title: 'Description',
    buttonText: 'Use this description',
    placeholder: 'Type scenario description...',
    info: (
      <>
        <Text>Write your scenario description using the tips below.</Text>
        <Text>
          <StyledList>
            <li>
              <strong>Use plain English</strong>
            </li>
            <li>
              <strong>Describe the site visitor's goal.</strong> For example:
              "When someone is seeking pricing information", or "When visitor is
              inquiring about the affiliate program".
            </li>
            <li>
              <strong>Provide identifying phrases.</strong> These are examples
              of what a chat visitor might say to trigger this scenario. For
              example: "How much does it cost?" or "Do you have a partner
              program?"
            </li>
          </StyledList>
        </Text>
      </>
    ),
  },
  instructions: {
    title: 'Reply guidelines',
    buttonText: 'Use these reply guidelines',
    placeholder: 'Type reply guidelines...',
    info: (
      <>
        <Text>Write your reply guidelines using the tips below.</Text>
        <Text>
          <StyledList>
            <li>
              <strong>Use plain English</strong>
            </li>
            <li>
              <strong>
                Provide instructions, rather than specific phrases.
              </strong>{' '}
              For example: "Do not answer the question directly. Instead, let
              them know that Drift takes security very seriously."
            </li>
            <li>
              <strong>
                When entering URLs, also provide a name for that page.
              </strong>{' '}
              For example: "Direct chat visitor to Pricing page:
              http://drift.com/pricing."
            </li>
          </StyledList>
        </Text>
      </>
    ),
  },
  exit: {
    title: 'Exit confirmation question',
    buttonText: 'Use this exit confirmation question',
    placeholder: 'Type exit confirmation question...',
    info: (
      <>
        <Text>
          An offramp is a way to exit out of the AI responses node and connect
          back into the main playbook. Before exiting the node, we'll first
          confirm with the site visitor using your exit confirmation question.
        </Text>
        <Text>Write your exit confirmation question using the tips below.</Text>
        <Text>
          <StyledList>
            <li>
              <strong>Use plain English</strong>
            </li>
            <li>
              <strong>
                Write your questions so that visitors answer with a simple "Yes"
                or "No."
              </strong>
            </li>
            <li>
              <strong>
                "Yes" answers should correspond with exiting the AI responses
                node.
              </strong>{' '}
              "No" answers will keep site visitors in the node and allow them to
              continue asking questions.
            </li>
          </StyledList>
        </Text>
      </>
    ),
  },
}

const InfoBox = ({ type }: { type: ModalType }) => (
  <InfoContainer>{configByType[type].info}</InfoContainer>
)

const Validation = ({
  prompt = '',
  response = '',
  type,
  onUse,
  divider = false,
}: {
  prompt: string
  response?: string
  type: ModalType
  onUse?: () => void
  divider?: boolean
}) => (
  <Flex direction="column" gap={2} css={{ marginTop: '$3' }}>
    {divider && <Divider />}
    <Text variant="bodyBold">{configByType[type].title}</Text>
    <PromptContainer>{prompt}</PromptContainer>
    <ResponseContainer>{response}</ResponseContainer>
    {!!onUse && (
      <Button onClick={onUse} variant="primary">
        {configByType[type].buttonText}
      </Button>
    )}
  </Flex>
)

const ValidationModal = ({
  isOpen,
  onClose,
  onAction,
  type,
  savedValidaton,
  defaultValidation,
}: ValidationModalProps) => {
  const [promptInput, setPromptInput] = useState('')
  const [validations, setValidations] = useState<ValidationObject[]>([])
  const [isSending, setIsSending] = useState(false)

  const bottomRef = useRef<null | HTMLDivElement>(null)
  const dispatch = useDispatch()

  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        block: 'end',
        behavior: 'smooth',
      })
    }
  }

  React.useEffect(() => {
    scrollToBottom()
  }, [bottomRef, isSending])

  const onValidateDescription = useMutation(validateDescription, {
    onSuccess: ({ data }) => {
      const { response, examples } = data
      setValidations([
        ...validations,
        { prompt: promptInput, response, examples },
      ])
      setPromptInput('')
      scrollToBottom()
      setIsSending(false)
    },
    onError: () => {
      setIsSending(false)
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not validate description`,
        })
      )
    },
  })

  const onValidateInstructions = useMutation(validateInstructions, {
    onSuccess: ({ data }) => {
      const { response, replyGuidelines } = data
      setValidations([
        ...validations,
        { prompt: promptInput, response, replyGuidelines },
      ])
      setPromptInput('')
      scrollToBottom()
      setIsSending(false)
    },
    onError: () => {
      setIsSending(false)
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not validate reply guidelines`,
        })
      )
    },
  })

  const onValidateExit = useMutation(validateExit, {
    onSuccess: ({ data: response }) => {
      setValidations([...validations, { prompt: promptInput, response }])
      setPromptInput('')
      scrollToBottom()
      setIsSending(false)
    },
    onError: () => {
      setIsSending(false)
      dispatch(
        showTransientNotification({
          type: 'error',
          message: `Could not validate exit confirmation question`,
        })
      )
    },
  })

  const onSend = () => {
    setIsSending(true)
    if (type === 'description') {
      onValidateDescription.mutate(promptInput)
    }
    if (type === 'instructions') {
      onValidateInstructions.mutate(promptInput)
    }
    if (type === 'exit') {
      onValidateExit.mutate(promptInput)
    }
  }

  return (
    <Modal.Root open={isOpen} onClose={onClose}>
      <Modal.Dialog fullWidth size="large" variant="info">
        <Modal.Content>
          <ScrollbarHiddenContainer>
            {savedValidaton && (
              <div>
                <Text variant="bodyBold" css={{ marginBottom: '$1' }}>
                  {' '}
                  Current {toLower(configByType[type].title)}
                </Text>
                <PromptContainer cssStyles={{ marginBottom: '$3' }}>
                  {savedValidaton}
                </PromptContainer>
              </div>
            )}
            <Container>
              <InfoBox type={type} />
              {defaultValidation && (
                <Validation
                  prompt={
                    defaultValidation.replyGuidelines ||
                    defaultValidation.prompt
                  }
                  response={defaultValidation.response}
                  type={type}
                  onUse={() => {
                    onClose()
                    onAction(
                      defaultValidation.prompt,
                      defaultValidation.examples,
                      defaultValidation.replyGuidelines
                    )
                  }}
                />
              )}
              {validations.map(
                ({ prompt, response, examples, replyGuidelines }, i) => (
                  <Validation
                    prompt={replyGuidelines || prompt}
                    response={response}
                    type={type}
                    onUse={() => {
                      onClose()
                      onAction(prompt, examples, replyGuidelines)
                    }}
                    divider={i > 0 || !!defaultValidation}
                  />
                )
              )}
              {isSending && promptInput && (
                <Validation
                  prompt={promptInput}
                  type={type}
                  divider={validations.length > 0 || !!defaultValidation}
                />
              )}
              <div ref={bottomRef}></div>
            </Container>
          </ScrollbarHiddenContainer>
        </Modal.Content>
        <Modal.Actions>
          <Flex direction="row" alignItems="center" width="100%">
            <TextareaWithButton
              buttonLabel="Send"
              placeholder={configByType[type].placeholder}
              value={promptInput}
              disabled={isSending}
              loading={isSending}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setPromptInput(e.target.value)
              }
              onClickButton={onSend}
            />
          </Flex>
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}

export default ValidationModal
